/**
 * Permission-aware components for controlling visibility and editability.
 *
 * Three states:
 * 1. Hidden (canView: false) - Component not rendered
 * 2. View-only (canView: true, canEdit: false) - Rendered but disabled/readonly
 * 3. Editable (canView: true, canEdit: true) - Full access
 *
 * Usage:
 *
 * // For sections that should hide/show
 * <Permissioned.Section canView={perms?.canView}>
 *   <MySection />
 * </Permissioned.Section>
 *
 * // For inputs that should be view-only or editable
 * <Permissioned.Input canEdit={perms?.canEdit}>
 *   <TextInput label="Name" value={name} onChange={setName} />
 * </Permissioned.Input>
 *
 * // For buttons that should hide when no permission
 * <Permissioned.Action canDo={perms?.canDelete}>
 *   <Button onClick={handleDelete}>Delete</Button>
 * </Permissioned.Action>
 */

import React from "react";

interface SectionProps {
  canView?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface InputProps {
  canEdit?: boolean;
  children: React.ReactElement;
}

interface ActionProps {
  canDo?: boolean;
  children: React.ReactNode;
}

/**
 * Conditionally renders a section based on view permission.
 * If canView is false, renders nothing (or fallback).
 */
function Section({ canView, children, fallback = null }: SectionProps) {
  if (canView === false) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}

/**
 * Wraps an input to make it disabled/readonly when canEdit is false.
 * The input is still visible but not editable.
 */
function Input({ canEdit, children }: InputProps) {
  if (canEdit === false) {
    const existingStyles = (
      children.props as { styles?: Record<string, unknown> }
    ).styles;
    const mergedProps = {
      disabled: true,
      readOnly: true,
      styles: {
        ...(typeof existingStyles === "object" && existingStyles !== null
          ? existingStyles
          : {}),
        input: {
          ...(typeof existingStyles?.input === "object" &&
          existingStyles?.input !== null
            ? existingStyles.input
            : {}),
          cursor: "not-allowed",
          backgroundColor: "var(--mantine-color-gray-1)",
        },
      },
    };
    return React.cloneElement(
      children,
      mergedProps as React.Attributes & Record<string, unknown>,
    );
  }
  return children;
}

/**
 * Conditionally renders an action (button, link, etc.) based on permission.
 * If canDo is false, renders nothing.
 */
function Action({ canDo, children }: ActionProps) {
  if (canDo === false) {
    return null;
  }
  return <>{children}</>;
}

export const Permissioned = {
  Section,
  Input,
  Action,
};

export default Permissioned;
